import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest, graphConfig } from '../config/authConfig';

// Cache duration in milliseconds (24 hours)
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// In-memory cache for instant access
let memoryCache: { [key: string]: string } = {};

interface CachedImage {
  data: string;
  timestamp: number;
}

// MSAL instance for token acquisition
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Generate a placeholder profile picture based on user initials
 */
const generatePlaceholderImage = (name: string): string => {
  const initials = name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);

  // Create a simple SVG placeholder
  const svg = `
    <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="50" fill="#3B82F6"/>
      <text x="50" y="50" font-family="Arial, sans-serif" font-size="36" font-weight="bold" 
            text-anchor="middle" dominant-baseline="central" fill="white">
        ${initials}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

/**
 * Fetch profile picture from Microsoft Graph API
 */
const fetchProfilePictureFromGraph = async (accessToken: string): Promise<string | null> => {
  try {
    const response = await fetch(graphConfig.graphPhotoEndpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (response.ok) {
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    }
    
    return null;
  } catch (error) {
    console.error('Failed to fetch profile picture from Graph:', error);
    return null;
  }
};

/**
 * Get access token for Microsoft Graph
 */
const getAccessToken = async (): Promise<string | null> => {
  try {
    await msalInstance.initialize();
    const accounts = msalInstance.getAllAccounts();
    
    if (accounts.length === 0) {
      return null;
    }

    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });

    return tokenResponse.accessToken;
  } catch (error) {
    console.error('Failed to acquire access token:', error);
    return null;
  }
};

/**
 * Preload profile picture for instant access
 */
export const preloadProfilePicture = async (userId?: string, userName?: string): Promise<void> => {
  try {
    // Check memory cache first
    const cacheKey = userId || 'default';
    if (memoryCache[cacheKey]) {
      return;
    }
    
    // Check localStorage cache
    const cachedImage = getFromCache(userId);
    if (cachedImage) {
      memoryCache[cacheKey] = cachedImage;
      return;
    }
    
    // Fetch and cache the picture
    const picture = await getProfilePicture(userId, userName);
    if (picture) {
      memoryCache[cacheKey] = picture;
    }
  } catch (error) {
    // Silent error handling
  }
};

/**
 * Get profile picture from Microsoft Graph or generate placeholder
 */
export const getProfilePicture = async (userId?: string, userName?: string): Promise<string | null> => {
  try {
    const cacheKey = userId || 'default';
    
    // Check memory cache first for instant access
    if (memoryCache[cacheKey]) {
      return memoryCache[cacheKey];
    }
    
    // Check if we have a cached version in localStorage
    const cachedImage = getFromCache(userId);
    if (cachedImage) {
      memoryCache[cacheKey] = cachedImage;
      return cachedImage;
    }

    // Try to get real profile picture from Microsoft Graph
    const accessToken = await getAccessToken();
    let profilePicture: string | null = null;
    
    if (accessToken) {
      profilePicture = await fetchProfilePictureFromGraph(accessToken);
    }
    
    // If no real picture available, generate placeholder
    if (!profilePicture) {
      const name = userName || 'User';
      profilePicture = generatePlaceholderImage(name);
    }
    
    // Cache the result
    if (profilePicture) {
      saveToCache(profilePicture, userId);
      memoryCache[cacheKey] = profilePicture;
    }
    
    return profilePicture;
  } catch (error) {
    console.error('Error getting profile picture:', error);
    
    // Fallback to placeholder
    const name = userName || 'User';
    const placeholder = generatePlaceholderImage(name);
    
    const cacheKey = userId || 'default';
    memoryCache[cacheKey] = placeholder;
    
    return placeholder;
  }
};

/**
 * Legacy function name for backward compatibility
 */
export const getAzureProfilePicture = getProfilePicture;

/**
 * Get profile picture instantly from memory cache (synchronous)
 */
export const getProfilePictureInstant = (userId?: string): string | null => {
  const cacheKey = userId || 'default';
  return memoryCache[cacheKey] || null;
};

/**
 * Clear profile picture cache
 */
export const clearProfilePictureCache = (userId?: string): void => {
  const cacheKey = userId || 'default';
  delete memoryCache[cacheKey];
  
  // Also clear localStorage cache
  const key = `profile_picture${userId ? `_${userId}` : ''}`;
  localStorage.removeItem(key);
};

/**
 * Save image to local storage cache
 */
const saveToCache = (base64: string, userId?: string): void => {
  try {
    const key = `profile_picture${userId ? `_${userId}` : ''}`;
    const data: CachedImage = {
      data: base64,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    // Silent error handling
  }
};

/**
 * Get image from local storage cache if valid
 */
const getFromCache = (userId?: string): string | null => {
  try {
    const key = `profile_picture${userId ? `_${userId}` : ''}`;
    const cached = localStorage.getItem(key);
    
    if (!cached) return null;
    
    const data: CachedImage = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is still valid
    if (now - data.timestamp < CACHE_DURATION) {
      return data.data;
    }
    
    // Cache expired, remove it
    localStorage.removeItem(key);
    return null;
  } catch (error) {
    return null;
  }
}; 